import React from 'react';
import { Card } from '@/components/ui/card';
import { Chart } from '../engine/chart';
import { Sparkles, Triangle, Square, Circle } from 'lucide-react';

interface Props { chart: Chart }

export const ChartPatterns: React.FC<Props> = ({ chart }) => {
  const p = chart.patterns;
  if (!p) return <p className="text-sm text-muted-foreground">No patterns detected.</p>;

  return (
    <div className="space-y-4">
      {(p.newMoon || p.fullMoon) && (
        <Card className="p-4 bg-card/50 flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5 text-primary" />
          <div>
            <h3 className="font-display text-base">{p.newMoon ? 'New Moon birth' : 'Full Moon birth'}</h3>
            <p className="text-sm text-muted-foreground">
              {p.newMoon
                ? 'Sun and Moon conjunct — identity and emotional nature are fused.'
                : 'Sun and Moon in opposition — built-in tension between identity and feeling.'}
            </p>
          </div>
        </Card>
      )}

      {p.stelliums.length > 0 && (
        <Card className="p-4 bg-card/50 space-y-2">
          <h3 className="font-display text-base">Stelliums</h3>
          {p.stelliums.map((s, i) => (
            <p key={i} className="text-sm">
              <span className="font-medium">
                {s.kind === 'sign' ? s.sign : `House ${s.house}`}
              </span>
              <span className="text-muted-foreground"> — {s.planets.join(', ')}</span>
            </p>
          ))}
        </Card>
      )}

      {p.aspectPatterns.length > 0 && (
        <Card className="p-4 bg-card/50 space-y-2">
          <h3 className="font-display text-base">Aspect patterns</h3>
          {p.aspectPatterns.map((ap, i) => (
            <div key={i} className="text-sm">
              <span className="font-medium">{ap.type}</span>
              <span className="text-muted-foreground"> — {ap.description}</span>
            </div>
          ))}
        </Card>
      )}

      <Card className="p-4 bg-card/50 space-y-3">
        <h3 className="font-display text-base">Balance</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Elements</p>
            <p>Fire {p.elements.Fire} · Earth {p.elements.Earth} · Air {p.elements.Air} · Water {p.elements.Water}</p>
            {p.elements.dominant && <p className="text-xs text-muted-foreground">Dominant: {p.elements.dominant}</p>}
            {p.elements.lacking.length > 0 && (
              <p className="text-xs text-muted-foreground">Lacking: {p.elements.lacking.join(', ')}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Modalities</p>
            <p>Cardinal {p.modalities.Cardinal} · Fixed {p.modalities.Fixed} · Mutable {p.modalities.Mutable}</p>
            {p.modalities.dominant && <p className="text-xs text-muted-foreground">Dominant: {p.modalities.dominant}</p>}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Polarity</p>
            <p>Masculine (Fire+Air) {p.polarities.masculine} · Feminine (Earth+Water) {p.polarities.feminine}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Chart shape</p>
            <p className="capitalize">{p.chartShape}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};